import { Injectable } from '@angular/core';

import { map, Observable, of, switchMap } from 'rxjs';

import { DistributionBlockTypeApiEnum } from '../../../api/distributions/enum/distribution-block-type-api.enum';
import { DistributionBlockApiModel } from '../../../api/distributions/model/distribution-block-api.model';
import { DistributionApiModel } from '../../../api/distributions/model/distribution-api.model';
import { DistributionFooterApiModel } from '../../../api/distributions/model/distribution-footer.api.model';
import { DistributionImageDisplayElementApiModel } from '../../../api/distributions/model/distribution-image-display-element-api.model';
import { DistributionTextDisplayElementApiModel } from '../../../api/distributions/model/distribution-text-display-element-api.model';
import { MediaServiceApi } from '../../../api/mediaService/media-service.api';
import { ReleaseStatus } from '../../../api/wire/enums/release-status.enum';
import { DeliverySchedule } from '../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { allowedFileTypes } from '../../../Common/Forms/models/file-upload.model';
import { ObservableTypeConverter } from '../../../Core/utils';
import { WireBlockTypeApiEnum } from '../../Wire/enums/wire-block-type.enum';
import { WireBlockApiModel } from '../../Wire/models/wire-block-api.model';
import { WireImageDisplayElementApiModel } from '../../Wire/models/wire-image-display-element-api.model';
import { WireModel } from '../../Wire/models/wire.model';
import { DistributionBlockApiModelToWireBlockApiModelConverter } from './distribution-block-api-to-wire-block-api.converter';


@Injectable()
export class DistributionApiToWireConverter implements ObservableTypeConverter<DistributionApiModel, WireModel> {
    private blockConverter: DistributionBlockApiModelToWireBlockApiModelConverter;
    // TODO: variable image limit.
    private imageCountLimit = 10;

    constructor(
        blockConverter: DistributionBlockApiModelToWireBlockApiModelConverter,
        private mediaServiceApi: MediaServiceApi
    ) {
        this.blockConverter = blockConverter;
    }

    convert(distribution: DistributionApiModel): Observable<WireModel> {
        return of(distribution).pipe(
            switchMap(dist => {
                const currentWire = new WireModel();
                currentWire.userNotes = null;
                currentWire.name = dist.name;
                currentWire.companyName = dist.footer?.company;
                currentWire.headline = dist.subjectLine; // TODO: remove special chars
                currentWire.subHeadline = dist.preheaderText;
                currentWire.dateline = this.convertFooterToDateline(dist.footer);
                currentWire.deliverySchedule = this.convertSchedulingToDeliverySchedule(
                    dist.sentNow,
                    dist.scheduledDate,
                    dist.timeZone
                );
                currentWire.contactName = dist.fromName;
                currentWire.contactEmail = dist.replyEmail;
                currentWire.status = ReleaseStatus.draft;

                const textBlocks = dist.blocks.filter(block => block.type === DistributionBlockTypeApiEnum.text);
                const imageBlocks: DistributionBlockApiModel[] = this.cutImageBlocksFromTextBlocks(textBlocks as DistributionTextDisplayElementApiModel[]);

                const convertedBlocks = dist.blocks
                    .map(block => this.blockConverter.convert(block))
                    .filter(block => block !== null);

                // TODO: Investigate way to split out image blocks in order
                // (ie., split a text block into an in-order representation of text and image blocks)
                const mappedImageBlocks = imageBlocks.map(block => this.blockConverter.convert(block));
                convertedBlocks.push(...mappedImageBlocks);

                if (dist.attachments.length && this.imageBlockCount(mappedImageBlocks) < this.imageCountLimit) {
                    return this.getImageAttachmentBlocks(dist.attachments).pipe(
                        map(attachmentImageBlocks => {
                            return {
                                currentWire: currentWire,
                                mappedBlocks: convertedBlocks,
                                attachmentImageBlocks: attachmentImageBlocks
                            };
                        })
                    );
                }

                return of({
                    currentWire: currentWire,
                    mappedBlocks: convertedBlocks,
                    attachmentImageBlocks: []
                });
            }),
            map(({currentWire, mappedBlocks, attachmentImageBlocks}) => {
                for (const imageBlock of attachmentImageBlocks) {
                    if (this.imageBlockCount(mappedBlocks) >= this.imageCountLimit) {
                        break;
                    }
                    mappedBlocks.push(imageBlock);
                }

                currentWire.mapBlocksFromApi(mappedBlocks);
                return currentWire;
            })
        );
    }

    private convertFooterToDateline(footer: DistributionFooterApiModel): string {
        if (!footer || !footer.city || !footer.province) {
            return '';
        }
        return `${footer.city.toLocaleUpperCase()}, ${footer.province}`;
    }

    private convertSchedulingToDeliverySchedule(sentNow: boolean, distScheduledDate: Date, timezone: string): DeliverySchedule {
        let scheduledDate: Date = null;
        let scheduledTimezone: string = null;

        // Only use distribution date if its set in the future
        if (!sentNow && new Date(distScheduledDate)?.getTime() > new Date().getTime()) {
            scheduledDate = new Date(distScheduledDate);
            scheduledTimezone = timezone;
        }
        const schedule = new DeliverySchedule(scheduledDate, scheduledTimezone);
        return schedule;
    }

    /**
     * Removes the images from the given text blocks (in place) and returns them as distinct image blocks
     */
    private cutImageBlocksFromTextBlocks(textBlocks: DistributionTextDisplayElementApiModel[]): DistributionImageDisplayElementApiModel[] {
        return textBlocks
            .map(block => {
                const blockDOM: Document = new DOMParser().parseFromString(block.text, 'text/html');
                const images: NodeListOf<HTMLImageElement> = blockDOM.querySelectorAll('img');
                const imageCopies: HTMLImageElement[] = [];
                images.forEach(image => {
                    imageCopies.push(image.cloneNode() as HTMLImageElement);
                    block.text = block.text.replace(image.outerHTML, '');
                });
                return imageCopies;
            })
            // FlatMap
            .reduce((allImages, curr) => {
                allImages.push(...curr.filter(el => !!el));
                return allImages;
            }, [])
            .map(imageElement => {
                return {
                    type: DistributionBlockTypeApiEnum.image,
                    url: imageElement.src
                } as DistributionImageDisplayElementApiModel;
            });
    }

    private getImageAttachmentBlocks(attachmentUuids: string[]): Observable<WireBlockApiModel[]> {
        return this.mediaServiceApi.getFiles(attachmentUuids).pipe(
            map(results => {
                return results.mediaResults
                    .filter(result => result.mimeType.match(allowedFileTypes.imageMIMETypesRegex))
                    .map(result => {
                        return {
                            type: WireBlockTypeApiEnum.image,
                            url: result.url,
                            displaySize: 1,
                        } as WireImageDisplayElementApiModel;
                    });
            })
        );
    }

    private imageBlockCount(blocks: WireBlockApiModel[]): number {
        return blocks
            .filter(block => block.type === WireBlockTypeApiEnum.image)
            .length;
    }

}
